/**
 * Utility to parse and normalize project budget_type across Resource Earning module.
 *
 * Supported budget types in PMS:
 * - "FIXED_COST", "FIXED", "FIXED COST" -> Fixed
 * - "CONTRACT" -> Contract
 * - "TIME_AND_MATERIAL", "TIME AND MATERIAL", "T&M" -> T&M(Contract)
 *
 * If not assigned, empty, null, undefined, or "NONE":
 * - isAssigned: false
 * - badgeLabel: null (renders NO badge)
 * - badgeClass: null
 * - model: null
 * - budgetType: null
 */
export const getBudgetTypeInfo = (rawBudget) => {
  if (
    !rawBudget ||
    typeof rawBudget !== "string" ||
    !rawBudget.trim() ||
    rawBudget.trim().toUpperCase() === "NONE"
  ) {
    return {
      isAssigned: false,
      isFixed: false,
      isContract: false,
      isTM: false,
      badgeLabel: null,
      badgeClass: null,
      model: null,
      budgetType: null,
    };
  }

  const normalized = rawBudget.trim().toUpperCase();

  if (
    normalized === "TIME_AND_MATERIAL" ||
    normalized === "TIME AND MATERIAL" ||
    normalized === "TIME_AND_M" ||
    normalized === "TIME AND M" ||
    normalized.startsWith("TIME_AND_") ||
    normalized.startsWith("TIME AND ") ||
    normalized === "T&M"
  ) {
    return {
      isAssigned: true,
      isFixed: false,
      isContract: true,
      isTM: true,
      badgeLabel: "T&M(Contract)",
      badgeClass: "badge-model-contract",
      model: "Contract",
      budgetType: "TIME_AND_MATERIAL",
    };
  }

  if (normalized === "CONTRACT") {
    return {
      isAssigned: true,
      isFixed: false,
      isContract: true,
      isTM: false,
      badgeLabel: "Contract",
      badgeClass: "badge-model-contract",
      model: "Contract",
      budgetType: "CONTRACT",
    };
  }

  if (
    normalized === "FIXED_COST" ||
    normalized === "FIXED" ||
    normalized === "FIXED COST"
  ) {
    return {
      isAssigned: true,
      isFixed: true,
      isContract: false,
      isTM: false,
      badgeLabel: "Fixed Cost",
      badgeClass: "badge-model-fixed",
      model: "Fixed",
      budgetType: "FIXED_COST",
    };
  }

  return {
    isAssigned: false,
    isFixed: false,
    isContract: false,
    isTM: false,
    badgeLabel: null,
    badgeClass: null,
    model: null,
    budgetType: null,
  };
};

/**
 * Priority for invoice status:
 * Allocated (3) > Mapped (2) > Unmapped (1) > Default (0)
 */
export const getStatusPriority = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "allocated") return 3;
  if (s === "mapped") return 2;
  if (s === "unmapped") return 1;
  return 0;
};

/**
 * Builds a fast lookup map of project IDs and project names to their current invoice allocation/mapping status.
 * Reusable across ManualEntryModal, ContractMappingModal, and FixedCostMappingModal.
 *
 * @param {Array} existingInvoices - List of invoices already available in UI/state
 * @param {Array} queueInvoices - List of fresh invoices from GET /invoices query
 * @param {Array} projectList - List of all projects from GET /invoices/projects
 * @returns {Object} map of { [projectIdOrTitleLower]: { status: "Allocated"|"Mapped", priority: number, invoiceNumber: string } }
 */
export const buildExistingProjectsMap = (
  existingInvoices = [],
  queueInvoices = [],
  projectList = []
) => {
  const map = {};
  const combined = [
    ...(Array.isArray(existingInvoices) ? existingInvoices : []),
    ...(Array.isArray(queueInvoices) ? queueInvoices : []),
  ];

  combined.forEach((inv) => {
    if (!inv) return;
    const pid = inv.project_id ?? inv.rawItem?.project_id;

    const matchedProj =
      Array.isArray(projectList) && pid
        ? projectList.find(
            (p) => String(p.project_id || p.id) === String(pid)
          )
        : null;

    const rawTitle =
      inv.project ||
      inv.project_name ||
      inv.project_title ||
      inv.rawItem?.project_name ||
      inv.rawItem?.project_title ||
      matchedProj?.title ||
      matchedProj?.project_name ||
      matchedProj?.name ||
      "";
    const pTitle =
      rawTitle === "—" || rawTitle === "-" ? "" : rawTitle.trim();

    const rawStatus = inv.status ?? inv.rawItem?.status ?? "";
    let statusFormatted = "";
    if (typeof rawStatus === "string" && rawStatus.trim()) {
      const sLower = rawStatus.trim().toLowerCase();
      if (sLower === "allocated") {
        statusFormatted = "Allocated";
      } else if (sLower === "mapped") {
        statusFormatted = "Mapped";
      } else if (sLower === "unmapped") {
        statusFormatted = "Unmapped";
      } else {
        statusFormatted =
          rawStatus.trim().charAt(0).toUpperCase() +
          rawStatus.trim().slice(1).toLowerCase();
      }
    } else {
      statusFormatted = inv.action === "View" ? "Mapped" : "Unmapped";
    }

    // Only show status badge for Allocated or Mapped projects
    if (statusFormatted !== "Allocated" && statusFormatted !== "Mapped") {
      return;
    }

    const priority = getStatusPriority(statusFormatted);
    const record = {
      status: statusFormatted,
      priority,
      invoiceNumber:
        inv.invoice ||
        inv.invoice_number ||
        inv.rawItem?.invoice_number ||
        "",
    };

    if (
      pid !== null &&
      pid !== undefined &&
      String(pid) !== "" &&
      String(pid) !== "0" &&
      String(pid) !== "null"
    ) {
      const pidStr = String(pid);
      if (!map[pidStr] || priority >= map[pidStr].priority) {
        map[pidStr] = record;
      }
    }

    if (matchedProj) {
      const mProjId = String(matchedProj.project_id || matchedProj.id || "");
      if (mProjId && (!map[mProjId] || priority >= map[mProjId].priority)) {
        map[mProjId] = record;
      }
    }

    if (pTitle) {
      const titleLower = pTitle.toLowerCase();
      if (!map[titleLower] || priority >= map[titleLower].priority) {
        map[titleLower] = record;
      }
    }
  });

  return map;
};
